import React, { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Plus, Trash2, Pencil, ArrowRightLeft, Download, Share2, Link2,
  FileText, Percent, Search, CheckCircle2, XCircle, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/brand/Logo";

const STATUS_TONE = {
  draft:    { label: "Draft",    color: "text-slate-400", dot: "bg-slate-500" },
  sent:     { label: "Sent",     color: "text-sky-400",   dot: "bg-sky-500" },
  accepted: { label: "Accepted", color: "text-emerald-400", dot: "bg-emerald-500 glow-green" },
  rejected: { label: "Rejected", color: "text-rose-400",  dot: "bg-rose-500" },
  expired:  { label: "Expired",  color: "text-slate-500", dot: "bg-slate-600" },
};
const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const emptyQuote = {
  title: "", client_id: "", reference: "",
  tax_rate: 0, discount_rate: 0, discount_amount: 0,
  status: "draft", notes: "", terms: "",
  valid_until: "",
  items: [{ description: "", quantity: 1, unit_price: 0 }],
};

function computeTotals(items, taxRate, discRate, discAmt) {
  const subtotal = (items || []).reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
  let disc = Number(discAmt || 0);
  if (!disc) disc = subtotal * Number(discRate || 0) / 100;
  disc = Math.min(disc, subtotal);
  const taxable = Math.max(0, subtotal - disc);
  const tax = taxable * Number(taxRate || 0) / 100;
  return { subtotal, discount: disc, taxable, tax, total: taxable + tax };
}

export default function Quotes() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyQuote);
  const [detail, setDetail] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [qs, cs] = await Promise.all([
        api.get("/quotes"),
        api.get("/crm/clients").catch(() => ({ data: [] })),
      ]);
      setItems(qs.data);
      setClients(cs.data);
    } catch (error) {
      console.error("Failed to load quotes:", error);
      toast.error("Failed to load quotes");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(
    () => computeTotals(form.items, form.tax_rate, form.discount_rate, form.discount_amount),
    [form.items, form.tax_rate, form.discount_rate, form.discount_amount],
  );
  const filtered = items.filter((it) => (it.title || "").toLowerCase().includes(q.toLowerCase()));

  const openNew = () => { setEditing(null); setForm(emptyQuote); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ...emptyQuote,
      ...row,
      items: (row.items && row.items.length ? row.items : emptyQuote.items).map((x) => ({
        description: x.description || "",
        quantity: Number(x.quantity ?? 1),
        unit_price: Number(x.unit_price ?? 0),
      })),
    });
    setOpen(true);
  };

  const updateItem = (i, k, v) => setForm({
    ...form,
    items: form.items.map((it, idx) => idx === i ? { ...it, [k]: k === "description" ? v : Number(v || 0) } : it),
  });
  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: form.title, client_id: form.client_id || undefined,
        reference: form.reference || undefined,
        tax_rate: Number(form.tax_rate || 0),
        discount_rate: Number(form.discount_rate || 0),
        discount_amount: Number(form.discount_amount || 0),
        notes: form.notes || undefined, terms: form.terms || undefined,
        valid_until: form.valid_until || undefined, status: form.status,
        items: form.items,
      };
      if (editing) await api.patch(`/quotes/${editing.quote_id}`, payload);
      else await api.post("/quotes", payload);
      toast.success(editing ? "Quote updated" : "Quote created");
      setOpen(false); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    try { await api.delete(`/quotes/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const convert = async (id) => {
    try { const { data } = await api.post(`/quotes/${id}/convert`);
      toast.success(`Converted to invoice ${data.invoice_id}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const share = async (row) => {
    try {
      const { data } = await api.post(`/quotes/${row.quote_id}/share`);
      setShareUrl(data.share_url);
      setDetail({ ...row, public_token: data.public_token });
      await navigator.clipboard.writeText(data.share_url).catch(() => {});
      toast.success("Share link copied");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/quotes/${id}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("PDF error");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `quote-${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Could not download PDF"); }
  };

  return (
    <DashboardLayout title="Quotes" subtitle="Professional estimates · discount · tax · PDF · e-approval · convert to invoice"
      right={<button data-testid="quotes-new" onClick={openNew} className="btn-scale h-9 px-3 rounded-md bg-blue-500 text-white font-medium text-sm flex items-center gap-2 hover:bg-blue-400"><Plus className="w-4 h-4" /> New quote</button>}>
      <div className="flex items-center gap-2 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="quotes-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title" className="w-full pl-9 pr-3 h-9 bg-slate-900 border border-slate-800 rounded-md text-sm text-white" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 overflow-x-auto" data-testid="quotes-table">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-800 bg-slate-900/40">
              <th className="p-3">Quote</th><th className="p-3">Client</th><th className="p-3">Total</th><th className="p-3">Valid until</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => {
              const s = STATUS_TONE[row.status] || STATUS_TONE.draft;
              return (
                <tr key={row.quote_id} className="border-b border-slate-800 hover:bg-slate-900/40 cursor-pointer" onClick={() => setDetail(row)} data-testid={`quote-row-${row.quote_id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-400" />
                      <div>
                        <div className="text-white">{row.title}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{row.reference || row.quote_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-slate-400 text-xs">{clients.find(c => c.client_id === row.client_id)?.name || "—"}</td>
                  <td className="p-3 text-white font-medium">{money(row.total)}</td>
                  <td className="p-3 text-slate-500 text-xs">{row.valid_until || "—"}</td>
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                      <span className={s.color}>{s.label}</span>
                    </span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={`quote-pdf-${row.quote_id}`} onClick={() => downloadPdf(row.quote_id)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-blue-300 hover:bg-slate-800 inline-flex" title="Download PDF"><Download className="w-3.5 h-3.5" /></button>
                    <button data-testid={`quote-share-${row.quote_id}`} onClick={() => share(row)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-sky-300 hover:bg-slate-800 inline-flex ml-1" title="Share for approval"><Share2 className="w-3.5 h-3.5" /></button>
                    <button data-testid={`quote-convert-${row.quote_id}`} onClick={() => convert(row.quote_id)} className="btn-scale h-8 px-2 rounded-md text-slate-300 hover:text-emerald-300 hover:bg-slate-800 inline-flex items-center gap-1 text-xs ml-1" title="Convert to invoice"><ArrowRightLeft className="w-3 h-3" /> Invoice</button>
                    <button data-testid={`quote-edit-${row.quote_id}`} onClick={() => openEdit(row)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-blue-300 hover:bg-slate-800 inline-flex ml-1"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`quote-del-${row.quote_id}`} onClick={() => remove(row.quote_id)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-rose-400 hover:bg-slate-800 inline-flex ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No quotes yet. Click <b>New quote</b> to create one.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Editor dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-4 h-4 text-blue-400" />{editing ? "Edit quote" : "New quote"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-2">
            <label className="md:col-span-2"><span className="overline block mb-1.5">Title</span>
              <input data-testid="quote-field-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" />
            </label>
            <label><span className="overline block mb-1.5">Reference</span>
              <input data-testid="quote-field-ref" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Q-2026-001" className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" />
            </label>

            <label><span className="overline block mb-1.5">Client</span>
              <select data-testid="quote-field-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-2 text-sm">
                <option value="">— select client —</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
              </select>
            </label>
            <label><span className="overline block mb-1.5">Valid until</span>
              <input data-testid="quote-field-valid" type="date" value={form.valid_until || ""} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" />
            </label>
            <label><span className="overline block mb-1.5">Status</span>
              <select data-testid="quote-field-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-2 text-sm">
                {Object.keys(STATUS_TONE).map((k) => <option key={k} value={k}>{STATUS_TONE[k].label}</option>)}
              </select>
            </label>

            <div className="md:col-span-3">
              <div className="flex items-center justify-between">
                <div className="overline">Services / line items</div>
                <button data-testid="quote-add-item" onClick={addItem} className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add item</button>
              </div>
              <div className="mt-2 rounded-md border border-slate-800 overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800 bg-slate-900/60">
                  <div className="col-span-6">Description</div>
                  <div className="col-span-2 text-right">Qty</div>
                  <div className="col-span-2 text-right">Unit price</div>
                  <div className="col-span-1 text-right">Amount</div>
                  <div className="col-span-1"></div>
                </div>
                {form.items.map((it, i) => (
                  <div key={`quote-item-${i}-${it.description?.slice(0, 12) || ""}`} className="grid grid-cols-12 gap-2 p-2 border-b border-slate-800 last:border-b-0" data-testid={`quote-item-${i}`}>
                    <input placeholder="Service description" value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} className="col-span-6 h-9 bg-slate-950 border border-slate-800 rounded px-2 text-sm" />
                    <input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="col-span-2 h-9 bg-slate-950 border border-slate-800 rounded px-2 text-sm text-right" />
                    <input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="col-span-2 h-9 bg-slate-950 border border-slate-800 rounded px-2 text-sm text-right" />
                    <div className="col-span-1 h-9 grid place-items-center text-right text-xs text-slate-300">{money(Number(it.quantity || 0) * Number(it.unit_price || 0))}</div>
                    <button onClick={() => removeItem(i)} className="col-span-1 h-9 grid place-items-center rounded text-slate-400 hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>

            <label className="md:col-span-1"><span className="overline block mb-1.5">Tax rate (%)</span>
              <div className="relative">
                <input data-testid="quote-field-tax" type="number" step="0.1" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 pr-8 text-sm" />
                <Percent className="w-3 h-3 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
            </label>
            <label className="md:col-span-1"><span className="overline block mb-1.5">Discount (%)</span>
              <input data-testid="quote-field-disc-rate" type="number" step="0.1" value={form.discount_rate} onChange={(e) => setForm({ ...form, discount_rate: e.target.value })} className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" />
            </label>
            <label className="md:col-span-1"><span className="overline block mb-1.5">Discount ($) — overrides %</span>
              <input data-testid="quote-field-disc-amt" type="number" step="0.01" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" />
            </label>

            <label className="md:col-span-3"><span className="overline block mb-1.5">Notes (visible to client)</span>
              <textarea data-testid="quote-field-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm" />
            </label>
            <label className="md:col-span-3"><span className="overline block mb-1.5">Terms &amp; conditions</span>
              <textarea data-testid="quote-field-terms" rows={3} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm" />
            </label>

            <div className="md:col-span-3 rounded-md border border-slate-800 bg-slate-950 p-4" data-testid="quote-totals">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><div className="overline">Subtotal</div><div className="text-white mt-1">{money(totals.subtotal)}</div></div>
                <div><div className="overline">Discount</div><div className="text-rose-300 mt-1">-{money(totals.discount)}</div></div>
                <div><div className="overline">Tax</div><div className="text-white mt-1">{money(totals.tax)}</div></div>
                <div><div className="overline">Total</div><div className="text-blue-400 mt-1 font-display text-lg font-semibold">{money(totals.total)}</div></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-slate-700 text-slate-300 text-sm">Cancel</button>
            <button data-testid="quote-save" disabled={busy} onClick={save} className="btn-scale h-9 px-4 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-400">{editing ? "Save changes" : "Create quote"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setShareUrl(""); } }}>
        <SheetContent side="right" className="bg-slate-900 border-slate-800 w-full sm:max-w-md p-0 overflow-y-auto text-white" data-testid="quote-detail">
          {detail && (
            <div className="p-6">
              <SheetHeader><SheetTitle className="text-white flex items-center gap-2"><FileText className="w-4 h-4 text-blue-400" /> {detail.title}</SheetTitle></SheetHeader>
              <div className="mt-1 text-xs text-slate-500 font-mono">{detail.reference || detail.quote_id}</div>
              <div className="mt-4 flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border border-slate-800 ${STATUS_TONE[detail.status]?.color || ""}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_TONE[detail.status]?.dot || "bg-slate-500"}`} />
                  {STATUS_TONE[detail.status]?.label || detail.status}
                </span>
                {detail.approval && <span className="text-[11px] text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {detail.approval.signed_name}</span>}
              </div>

              <dl className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Client</dt><dd>{clients.find((c) => c.client_id === detail.client_id)?.name || "—"}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd>{money(detail.subtotal)}</dd></div>
                {detail.discount > 0 && <div className="flex justify-between"><dt className="text-slate-500">Discount</dt><dd className="text-rose-300">-{money(detail.discount)}</dd></div>}
                <div className="flex justify-between"><dt className="text-slate-500">Tax ({detail.tax_rate || 0}%)</dt><dd>{money(detail.tax)}</dd></div>
                <div className="flex justify-between text-blue-400 font-medium"><dt>Total</dt><dd>{money(detail.total)}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Valid until</dt><dd>{detail.valid_until || "—"}</dd></div>
              </dl>

              <div className="mt-6 space-y-2">
                <button data-testid="detail-share" onClick={() => share(detail)} className="btn-scale w-full h-10 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium flex items-center justify-center gap-2"><Share2 className="w-4 h-4" /> Share for e-approval</button>
                <button data-testid="detail-pdf" onClick={() => downloadPdf(detail.quote_id)} className="btn-scale w-full h-10 rounded-md border border-slate-700 hover:bg-slate-800 text-white text-sm flex items-center justify-center gap-2"><Download className="w-4 h-4" /> Download branded PDF</button>
                <button data-testid="detail-convert" onClick={() => convert(detail.quote_id)} className="btn-scale w-full h-10 rounded-md border border-slate-700 hover:bg-slate-800 text-white text-sm flex items-center justify-center gap-2"><ArrowRightLeft className="w-4 h-4" /> Convert to invoice</button>
              </div>

              {shareUrl && (
                <div className="mt-6 rounded-md border border-slate-800 bg-slate-950 p-3">
                  <div className="overline mb-2 flex items-center gap-1"><Link2 className="w-3 h-3" /> Public approval link</div>
                  <div className="flex items-center gap-2">
                    <input readOnly value={shareUrl} className="flex-1 h-9 bg-slate-900 border border-slate-800 rounded px-2 text-xs font-mono" />
                    <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }} className="btn-scale h-9 w-9 grid place-items-center rounded-md border border-slate-700 hover:bg-slate-800" data-testid="detail-copy"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                  <a href={shareUrl} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-blue-400 hover:text-blue-300">Open preview →</a>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </DashboardLayout>
  );
}
